import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest, apiTokenRequest } from "./msalConfig.js";

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthInner({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      setUser({
        id: account.localAccountId,
        email: account.username,
        name: account.name || account.username.split("@")[0],
        firstName: account.name?.split(" ")[0] || "",
        lastName: account.name?.split(" ").slice(1).join(" ") || "",
      });
      setLoading(false);
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [isAuthenticated, accounts]);

  const login = useCallback(async () => {
    try {
      // Request the API scope alongside Graph at sign-in so the API access token is
      // consented and cached up front. That lets getApiToken() acquire it SILENTLY on
      // every /api call — no interactive redirect from a data fetch (which would wedge
      // MSAL). The client is pre-authorized for this scope, so sign-in stays one click.
      await instance.loginRedirect({
        ...loginRequest,
        scopes: [...loginRequest.scopes, ...apiTokenRequest.scopes],
      });
    } catch (e) {
      console.error("Login failed:", e);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    } catch (e) {
      console.error("Logout failed:", e);
    }
  }, [instance]);

  const getToken = useCallback(async () => {
    if (!accounts[0]) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(loginRequest);
      }
      throw e;
    }
  }, [instance, accounts]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ msalInstance, children }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthInner>{children}</AuthInner>
    </MsalProvider>
  );
}
